import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of } from 'rxjs';

import { ErrorService } from '@ninety/platform/services/services/error.service';

import { FollowersActions } from './followers.actions';
import { FollowersService } from './followers.service';

@Injectable()
export class FollowersEffects {
  private actions$ = inject(Actions);
  private followersService = inject(FollowersService);
  private errorService = inject(ErrorService);

  initFollowers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowersActions.init),
      map(({ entityType, entityId }) => FollowersActions.getFollowers({ entityType, entityId }))
    )
  );

  getFollowers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowersActions.getFollowers),
      exhaustMap(({ entityType, entityId }) =>
        this.followersService.getFollowers(entityType, entityId).pipe(
          map(followers => FollowersActions.getFollowersSuccess({ followers })),
          catchError((error: unknown) =>
            of(FollowersActions.getFollowersFailure({ error, customMessage: 'Failed to get followers.' }))
          )
        )
      )
    )
  );

  getFollowersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FollowersActions.getFollowersFailure),
        map(({ customMessage }) => {
          this.errorService.notify(customMessage);
        })
      ),
    { dispatch: false }
  );

  addFollowers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowersActions.addFollowers),
      exhaustMap(({ entityType, entityId, followerIds }) =>
        this.followersService.addFollowers(entityType, entityId, followerIds).pipe(
          map(followers => FollowersActions.addFollowersSuccess({ followers })),
          catchError((error: unknown) =>
            of(FollowersActions.addFollowersFailure({ error, customMessage: 'Failed to add followers.' }))
          )
        )
      )
    )
  );

  addFollowersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FollowersActions.addFollowersFailure),
        map(({ customMessage }) => {
          this.errorService.notify(customMessage);
        })
      ),
    { dispatch: false }
  );

  removeFollower$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FollowersActions.removeFollower),
      exhaustMap(({ entityType, entityId, userId }) =>
        this.followersService.removeFollower(entityType, entityId, userId).pipe(
          map(followers => FollowersActions.removeFollowerSuccess({ followers })),
          catchError((error: unknown) =>
            of(FollowersActions.removeFollowerFailure({ error, customMessage: 'Failed to remove follower.' }))
          )
        )
      )
    )
  );

  removeFollowerFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FollowersActions.removeFollowerFailure),
        map(({ customMessage }) => {
          this.errorService.notify(customMessage);
        })
      ),
    { dispatch: false }
  );
}
